import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';

import { Product, Service, ShoppingCartItem } from '../_appCommon/base/AppInterfaceAndBaseClasses';
import { BroadcastManager } from '../_appCommon/base/BroadcastManager';
import { AppEventNames, IVA } from '../_appCommon/base/Constants';

@Injectable({ providedIn: 'root'})
export class ShoppingCartDataService
{
  private _shoppingCartName = 'shoppingCart';

  public caseId: number = null;
  public casePatientName: string = null;

  public currentShoppingCart: ShoppingCartItem[];
  private currentShoppingCart$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  public showShoppingCart:BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public unsubscribeAdditionalServices: Subject<boolean> = new Subject();
  public unsubscribeShoppingCart: Subject<boolean> = new Subject();

  public lastOrderId:number = null;

  isCompleteCreateAddedServices$ = new Subject<boolean>();

  private _percentageForMSIUsage = new BehaviorSubject<number>(0)
  private percentageForMSIUsageOf = this._percentageForMSIUsage.asObservable();

  constructor() {
    this.setIsCompleteCreateAddedServices(false);
  }

  public setPercentageForMSIUsage(value: number): void {
    this._percentageForMSIUsage.next(value);
  }

  public getPercentageForMSIUsage(): Observable<number> {
    return this.percentageForMSIUsageOf;
  }

  setIsCompleteCreateAddedServices(value : boolean): void
  {
    this.isCompleteCreateAddedServices$.next(value);
  }

  public getItems(): Observable<ShoppingCartItem[]>
  {
    return this.currentShoppingCart$.asObservable();
  }

  public getCountItems(): number
  {
    return this.currentShoppingCart.length;
  }

  public getSubtotal(): number
  {
    return this.currentShoppingCart?.reduce((accumulator, currentValue) =>
    {
      return accumulator
      + (currentValue.item.itemPrices?.subtotal
      + (this.isProduct(currentValue.item) ? this.includeAdditionalServices(currentValue.item as Product) : 0)) * currentValue.quantity;
    }, 0);
  }

  private includeAdditionalServices(item: Product): number
  {
    return (this.getTotalDentalMonitoring(item)
        + this.getTotalComplexCase(item)
        + this.getTotalExtraAligners(item));
  }

  public getTax(): number
  {
    //Tax for products only
    let taxProducts = 0;
    //Tax for additional services
    let taxProductAdditionalServices = 0;

    if(this.currentShoppingCart){
      taxProducts =  this.currentShoppingCart?.reduce((accumalator, currentValue) =>
      {
        return accumalator + (currentValue.item.itemPrices.totalTax * currentValue.quantity);
      }, 0);

      for (let i = 0; i < this.currentShoppingCart.length; i++)
      {
        if (this.isProduct(this.currentShoppingCart[i].item))
        {
          let item = this.currentShoppingCart[i].item;
          taxProductAdditionalServices += item.additionalServices.addDentalMonitoring ? (this.currentShoppingCart[i].quantity * (item.itemPrices.priceExtraDentalMonitoring / (1 + IVA)) * IVA) : 0;
          taxProductAdditionalServices += item.additionalServices.addIsComplexCase ? (this.currentShoppingCart[i].quantity *(item.itemPrices.priceComplexCase / (1 + IVA)) * IVA) : 0;
          taxProductAdditionalServices += item.additionalServices.hasUnlimitedAligners ? 0 : (this.currentShoppingCart[i].quantity * ((item.itemPrices.priceExtraAligner / (1 + IVA)) * IVA * item.additionalServices.extraAlignersQuantity));
        }
      }

    }
    
    return taxProducts + taxProductAdditionalServices;
  }

  public getTotal(): number
  {
    return this.getTax() + this.getSubtotal();
  }

  private getTotalDentalMonitoring(item: Product): number
  {
    return item.additionalServices.addDentalMonitoring ? item.itemPrices.priceExtraDentalMonitoring  / (1 + IVA) : 0;
  }

  private getTotalComplexCase(item: Product): number
  {
    return item.additionalServices.addIsComplexCase ? item.itemPrices.priceComplexCase / (1 + IVA) : 0;
  }

  private getTotalExtraAligners(item: Product): number
  {
    return item.additionalServices.hasUnlimitedAligners ? 0 : (item.itemPrices.priceExtraAligner / (1 + IVA)) * item.additionalServices.extraAlignersQuantity;
  }

  public onAddItem(item: Service | Product, quantity: number): string
  {
    if (item)
    {
      const newShoppingServiceProduct: ShoppingCartItem = { quantity: quantity, item: item };

      this.currentShoppingCart.push(newShoppingServiceProduct);

      this.saveChanges();

      return 'Producto agregado correctamente';
    }

  }

  public onUpdateQuantityItem(itemIndex: number, quantity: number)
  {
    if (itemIndex != -1)
    {
      this.currentShoppingCart[itemIndex].quantity = quantity;
      this.saveChanges();
    }
  }

  public onDeleteItem(itemIndex: number): string
  {
    if(itemIndex != -1)
    {
      this.currentShoppingCart.splice(itemIndex, 1);
      this.saveChanges();

      if(this.getCountItems() == 0)
      {
        this.caseId = null;
      }

      return 'Se ha eliminado el producto del carrito';
    }
  }

  public onClearItems(): void
  {
    this.lastOrderId = null;
    this.currentShoppingCart.clear();
    this.saveChanges();
  }

  public saveChanges(): void
  {
    localStorage.setItem(this._shoppingCartName, JSON.stringify(this.currentShoppingCart));
    this.currentShoppingCart$.next(this.currentShoppingCart);
  }

  public initializeShoppingCart(): void
  {
    this.currentShoppingCart = JSON.parse(localStorage.getItem(this._shoppingCartName));

    this.currentShoppingCart = this.currentShoppingCart ? this.currentShoppingCart : [];
    this.saveChanges();
  }

  public onShowShoppingCart(show: boolean): void
  {
    this.showShoppingCart.next(show);
  }

  public currentCartHasService(): boolean
  {
    return this.currentShoppingCart.some(x => !this.isProduct(x.item));
  }

  public isProduct(item: Product | Service): item is Product
  {
    return (item as Product).productType !== undefined;
  }

  public onChargeDoneServices(): void
  {
    BroadcastManager.Instance.broadcast(AppEventNames.onChargeDoneServices, this.currentShoppingCart, this.lastOrderId);
  }

  getTotalByItem(itemCart: any): number{
    return this.getTaxByItem(itemCart) + this.getSubtotalByItem(itemCart);
  }

  getTaxByItem(itemCart: any): number{
    //Tax for products only
    let taxProducts = 0;
    //Tax for additional services
    let taxProductAdditionalServices = 0;

    taxProducts = itemCart.item.itemPrices.totalTax * itemCart.quantity

    let item = itemCart.item;
    taxProductAdditionalServices += item.additionalServices.addDentalMonitoring ? (itemCart.quantity * (item.itemPrices.priceExtraDentalMonitoring / (1 + IVA)) * IVA) : 0;
    taxProductAdditionalServices += item.additionalServices.addIsComplexCase ? (itemCart.quantity *(item.itemPrices.priceComplexCase / (1 + IVA)) * IVA) : 0;
    taxProductAdditionalServices += item.additionalServices.hasUnlimitedAligners ? 0 : (itemCart.quantity * ((item.itemPrices.priceExtraAligner / (1 + IVA)) * IVA * item.additionalServices.extraAlignersQuantity));
 
    return taxProducts + taxProductAdditionalServices;
  }

  getSubtotalByItem(itemCart: any): number{
    return (itemCart.item.itemPrices?.subtotal
      + (this.isProduct(itemCart.item) ? this.includeAdditionalServices(itemCart.item as Product) : 0)) * itemCart.quantity;
  }
}
